import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronUp, ChevronDown, Star, Plus, Trash2, ImageIcon } from 'lucide-react';
import { adminProducts } from '../../services/api';
import AddFeaturedModal from '../../components/admin/AddFeaturedModal';

const API_URL = process.env.REACT_APP_API_URL || '';
const imgSrc = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${API_URL}${url}`;
};

export default function FeaturedProducts() {
  const [products, setProducts]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving]         = useState({}); // { [productID]: bool }
  const [toastMsg, setToastMsg]     = useState('');
  const toastTimer = useRef(null);

  const showToast = (msg) => {
    setToastMsg(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(''), 4000);
  };

  // ── Load ──────────────────────────────────────────────────────────────────────
  const load = useCallback(() => {
    setLoading(true);
    adminProducts
      .getFeatured()
      .then((res) => { if (res.data?.success) setProducts(res.data.data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Move up / down ────────────────────────────────────────────────────────────
  const handleMove = useCallback(async (index, dir) => {
    const swapIdx = index + dir;
    if (swapIdx < 0 || swapIdx >= products.length) return;

    // Swap positions in list and reassign sequential sort orders
    const newList = [...products];
    [newList[index], newList[swapIdx]] = [newList[swapIdx], newList[index]];
    const withOrders = newList.map((p, i) => ({ ...p, featuredSortOrder: i + 1 }));

    setProducts(withOrders); // optimistic

    const a = withOrders[index];
    const b = withOrders[swapIdx];

    try {
      await Promise.all([
        adminProducts.updateFeatured(a.productID, { isFeatured: true, featuredSortOrder: a.featuredSortOrder }),
        adminProducts.updateFeatured(b.productID, { isFeatured: true, featuredSortOrder: b.featuredSortOrder }),
      ]);
    } catch {
      setProducts(products); // revert on error
    }
  }, [products]);

  // ── Remove from featured ──────────────────────────────────────────────────────
  const handleRemove = useCallback(async (product) => {
    setSaving((prev) => ({ ...prev, [product.productID]: true }));
    try {
      await adminProducts.updateFeatured(product.productID, { isFeatured: false });
      setProducts((prev) => prev.filter((p) => p.productID !== product.productID));
      showToast('Removed from featured');
    } catch {
      // silent
    } finally {
      setSaving((prev) => ({ ...prev, [product.productID]: false }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Add from modal ────────────────────────────────────────────────────────────
  const handleAdd = useCallback(async (product) => {
    const maxOrder = products.reduce((m, p) => Math.max(m, p.featuredSortOrder ?? 0), 0);
    const nextOrder = maxOrder + 1;

    try {
      await adminProducts.updateFeatured(product.productID, {
        isFeatured: true,
        featuredSortOrder: nextOrder,
      });
      setProducts((prev) => [
        ...prev,
        { ...product, isFeatured: 1, featuredSortOrder: nextOrder },
      ]);
      showToast('Added to featured');
    } catch {
      // silent
    }
  }, [products]); // eslint-disable-line react-hooks/exhaustive-deps

  const existingIds = products.map((p) => p.productID);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="container-fluid py-4" style={{ maxWidth: 760 }}>

      {/* Header */}
      <div className="d-flex align-items-start justify-content-between mb-1">
        <div>
          <h4 className="mb-0 d-flex align-items-center gap-2">
            <Star size={20} className="text-warning" />
            Featured Products
          </h4>
          <p className="text-muted small mb-0 mt-1">
            Drag to reorder · up to 8 products
          </p>
        </div>
        <button
          className="btn btn-primary btn-sm d-flex align-items-center gap-1"
          disabled={products.length >= 8}
          onClick={() => setShowAddModal(true)}
        >
          <Plus size={14} />
          Add Product
        </button>
      </div>

      {/* Limit warning */}
      {products.length >= 8 && (
        <div className="alert alert-warning py-2 small mt-3">
          You have 8 featured products — the maximum. Remove one to add another.
        </div>
      )}

      {/* List */}
      <div className="card mt-3">
        {loading ? (
          <div className="card-body text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
          </div>
        ) : products.length === 0 ? (
          <div className="card-body text-center py-5">
            <Star size={40} className="text-muted mb-3 opacity-25" />
            <p className="text-muted mb-3">No featured products yet. Click Add Product to get started.</p>
            <button
              className="btn btn-primary btn-sm d-flex align-items-center gap-1 mx-auto"
              onClick={() => setShowAddModal(true)}
            >
              <Plus size={14} />
              Add Product
            </button>
          </div>
        ) : (
          <div className="list-group list-group-flush">
            {products.map((p, idx) => {
              const isSaving = saving[p.productID] || false;
              const thumb = imgSrc(p.proLabelImageUrl);

              return (
                <div
                  key={p.productID}
                  className="list-group-item d-flex align-items-center gap-3 py-2"
                >
                  {/* Sort order badge */}
                  <span
                    className="badge bg-secondary"
                    style={{ width: 24, textAlign: 'center', flexShrink: 0 }}
                  >
                    {p.featuredSortOrder}
                  </span>

                  {/* Thumbnail */}
                  {thumb ? (
                    <img
                      src={thumb}
                      alt=""
                      style={{ width: 40, height: 56, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
                    />
                  ) : (
                    <div
                      className="bg-light d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{ width: 40, height: 56, borderRadius: 4 }}
                    >
                      <ImageIcon size={16} className="text-muted" />
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-grow-1">
                    <div className="fw-semibold small lh-sm">{p.proFanciful || '—'}</div>
                    <div className="text-muted" style={{ fontSize: 11 }}>
                      {[p.proVintage, p.proVarietal].filter(Boolean).join(' · ')}
                    </div>
                  </div>

                  {/* Up / Down / Remove */}
                  <div className="d-flex align-items-center gap-1 flex-shrink-0">
                    <button
                      className="btn btn-outline-secondary btn-sm p-1"
                      style={{ lineHeight: 1 }}
                      disabled={idx === 0 || isSaving}
                      title="Move up"
                      onClick={() => handleMove(idx, -1)}
                    >
                      <ChevronUp size={14} />
                    </button>
                    <button
                      className="btn btn-outline-secondary btn-sm p-1"
                      style={{ lineHeight: 1 }}
                      disabled={idx === products.length - 1 || isSaving}
                      title="Move down"
                      onClick={() => handleMove(idx, 1)}
                    >
                      <ChevronDown size={14} />
                    </button>
                    <button
                      className="btn btn-outline-danger btn-sm p-1 ms-1"
                      style={{ lineHeight: 1 }}
                      disabled={isSaving}
                      title="Remove from featured"
                      onClick={() => handleRemove(p)}
                    >
                      {isSaving ? (
                        <span className="spinner-border spinner-border-sm" role="status" style={{ width: 14, height: 14 }} />
                      ) : (
                        <Trash2 size={14} />
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add modal */}
      {showAddModal && (
        <AddFeaturedModal
          existingIds={existingIds}
          onAdd={handleAdd}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {/* Toast */}
      {toastMsg && (
        <div className="toast-container position-fixed bottom-0 end-0 p-3" style={{ zIndex: 9999 }}>
          <div className="toast show align-items-center text-bg-success border-0">
            <div className="d-flex">
              <div className="toast-body">{toastMsg}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
